import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import StorefrontPolicyCheck from "@/components/StorefrontPolicyCheck";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MessageCircle, Award, ShieldCheck, MapPin, ArrowRight, Sparkles, Search, ExternalLink, Loader2, CheckCircle2 } from "lucide-react";
import { CreceLogo } from "@/components/CreceLogo";
import { ProductImage } from "@/components/ProductImage";
import { getCategoryMeta } from "@/lib/categoryMeta";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function ProductCard({ p, onApply }) {
  const meta = getCategoryMeta(p.category);
  const Icon = meta.icon;
  return (
    <button
      type="button"
      onClick={() => onApply(p)}
      data-testid={`storefront-product-${p.id}`}
      className="group text-left bg-white rounded-2xl border border-stone-200 overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all"
    >
      <div className="relative">
        <ProductImage product={p} className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className={`absolute top-3 left-3 ${meta.bg} ${meta.text} w-11 h-11 rounded-xl grid place-items-center shadow-lg`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className={`absolute top-3 right-3 ${meta.badge} text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full`}>
          {p.category}
        </div>
        {p.poster_meta && (
          <div className="absolute bottom-2 left-2 bg-emerald-900 text-amber-300 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full shadow-md inline-flex items-center gap-1" data-testid={`storefront-product-${p.id}-poster-badge`}>
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>
            Poster
          </div>
        )}
      </div>
      <div className="p-6">
        <h3 className="font-display text-xl font-bold tracking-tight line-clamp-2">{p.name}</h3>
        <p className="text-sm text-stone-500 mt-1">{p.bank}</p>
        <div className="mt-5 pt-5 border-t border-stone-100 flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-sm font-bold text-emerald-900" data-testid={`storefront-apply-${p.id}`}>
            Apply now
          </span>
          <ArrowRight className="w-5 h-5 text-stone-400 group-hover:text-emerald-900 group-hover:translate-x-1 transition-all" />
        </div>
      </div>
    </button>
  );
}

function ApplyDialog({ open, product, code, partnerName, onClose }) {
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { lead_id, apply_url }

  useEffect(() => { if (open) { setName(""); setMobile(""); setEmail(""); setCity(""); setSubmitting(false); setDone(null); } }, [open]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (submitting || !product) return;
    if (!name.trim() || name.trim().length < 2) { toast.error("Please enter your full name"); return; }
    if (mobile.length !== 10) { toast.error("Enter a valid 10-digit mobile"); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${BACKEND}/api/c/${code}/lead`, {
        product_id: product.id, customer_name: name.trim(), customer_mobile: mobile,
        customer_email: email.trim() || null, city: city.trim() || null,
      });
      setDone(r.data);
      // Auto-open bank page in new tab after a short beat
      if (r.data.apply_url) setTimeout(() => window.open(r.data.apply_url, "_blank", "noopener"), 600);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit — please try again");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="storefront-apply-dialog">
        {!done ? (
          <>
            <DialogHeader>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-800">Apply</div>
              <DialogTitle className="font-display text-2xl font-black mt-1">{product?.name}</DialogTitle>
              <p className="text-sm text-stone-500 mt-1">Your details go directly to {partnerName} — they{"'"}ll guide you through the rest.</p>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3 mt-2">
              <div>
                <Label className="font-semibold text-sm">Full name *</Label>
                <Input data-testid="storefront-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11 rounded-lg" placeholder="e.g. Aman Sharma" autoFocus />
              </div>
              <div>
                <Label className="font-semibold text-sm">Mobile *</Label>
                <div className="flex gap-2 mt-1.5">
                  <div className="h-11 px-3 grid place-items-center bg-stone-100 border border-stone-200 rounded-lg font-semibold text-sm">+91</div>
                  <Input data-testid="storefront-mobile-input" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))} className="h-11 rounded-lg flex-1" inputMode="numeric" placeholder="98XXXXXXXX" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-semibold text-sm">City <span className="font-normal text-stone-400">(optional)</span></Label>
                  <Input value={city} onChange={(e) => setCity(e.target.value)} className="mt-1.5 h-11 rounded-lg" placeholder="Mumbai" />
                </div>
                <div>
                  <Label className="font-semibold text-sm">Email <span className="font-normal text-stone-400">(optional)</span></Label>
                  <Input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11 rounded-lg" placeholder="you@…" type="email" />
                </div>
              </div>
              <Button type="submit" disabled={submitting} data-testid="storefront-submit-btn"
                className="w-full rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold h-12 mt-2">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : <>Submit & continue to bank <ArrowRight className="ml-2 w-4 h-4" /></>}
              </Button>
              <p className="text-[10px] text-stone-400 text-center leading-relaxed">By submitting you agree to be contacted by {partnerName} about this product. We don{"'"}t spam.</p>
            </form>
          </>
        ) : (
          <div className="py-3 text-center" data-testid="storefront-apply-success">
            <div className="w-14 h-14 rounded-full bg-emerald-50 border-2 border-emerald-200 mx-auto grid place-items-center mb-3">
              <CheckCircle2 className="w-7 h-7 text-emerald-700" />
            </div>
            <h3 className="font-display text-2xl font-black">You{"'"}re all set!</h3>
            <p className="text-stone-500 text-sm mt-2">{partnerName} just got your details. We{"'"}ve opened {product?.name}{"'"}s application page in a new tab — finish that and you{"'"}re done.</p>
            {done.apply_url && (
              <a href={done.apply_url} target="_blank" rel="noreferrer">
                <Button className="rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold mt-5">
                  <ExternalLink className="w-4 h-4 mr-2" /> Open application again
                </Button>
              </a>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ClientStorefront() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [activeCat, setActiveCat] = useState("ALL");
  const [search, setSearch] = useState("");
  const [applying, setApplying] = useState(null);

  useEffect(() => {
    const c = (code || "").trim().toUpperCase();
    if (!c) { setError("Invalid referral code"); return; }
    axios.get(`${BACKEND}/api/c/${c}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Storefront not found"));
  }, [code]);

  const filteredProducts = useMemo(() => {
    if (!data) return [];
    const all = activeCat === "ALL"
      ? data.categories.flatMap((c) => data.products_by_category[c] || [])
      : (data.products_by_category[activeCat] || []);
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((p) => `${p.name} ${p.bank} ${p.description || ""}`.toLowerCase().includes(q));
  }, [data, activeCat, search]);

  const onApply = (product) => {
    setApplying(product);
    // Fire-and-forget click tracking — feeds share analytics with channel='storefront'
    try {
      axios.post(`${BACKEND}/api/c/${code.toUpperCase()}/track-click`, { product_id: product.id }).catch(() => {});
    } catch { /* ignore */ }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-stone-50 grid place-items-center p-6" data-testid="storefront-error">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-100 mx-auto grid place-items-center mb-4"><Sparkles className="w-7 h-7 text-red-600" /></div>
          <h1 className="font-display text-2xl font-black">Storefront not found</h1>
          <p className="text-stone-500 mt-2 text-sm">{error}</p>
          <Link to="/"><Button className="mt-6 rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold">Go home</Button></Link>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-stone-50 grid place-items-center text-stone-400">Loading…</div>;

  const p = data.partner;
  const initials = (p.name || "").split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  const waNumber = p.whatsapp_number ? `91${p.whatsapp_number}` : "";
  const waMsg = encodeURIComponent(`Hi ${p.name}, I'm browsing your Crece store and would like to know more about your products.`);
  const waUrl = waNumber ? `https://wa.me/${waNumber}?text=${waMsg}` : "";

  return (
    <div className="min-h-screen bg-stone-50" data-testid="client-storefront">
      {/* Top bar */}
      <header className="px-5 sm:px-8 py-5 flex items-center justify-between bg-white border-b border-stone-200 sticky top-0 z-40 backdrop-blur supports-[backdrop-filter]:bg-white/85">
        <Link to="/" className="flex items-center gap-2">
          <CreceLogo size="sm" theme="dark" />
          <span className="font-display font-black text-lg tracking-tight">Crece</span>
        </Link>
        {waUrl && (
          <a href={waUrl} target="_blank" rel="noreferrer" data-testid="storefront-wa-top">
            <Button size="sm" className="rounded-full bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold">
              <MessageCircle className="w-4 h-4 mr-1.5" /> Chat
            </Button>
          </a>
        )}
      </header>

      {/* "Recently promoted" celebration banner — full-width, just under the
          top bar so it's the first thing visitors see. Shown for 30 days after
          each rank-up. */}
      {p.promoted_at && Date.now() - new Date(p.promoted_at).getTime() < 30 * 24 * 60 * 60 * 1000 && (
        <div className="bg-gradient-to-r from-amber-400 via-amber-500 to-amber-400 text-emerald-950 px-5 sm:px-8 py-3 text-center font-bold text-sm flex items-center justify-center gap-3 flex-wrap" data-testid="storefront-promoted-banner">
          <span className="text-xl">🎉</span>
          <span>
            <span className="opacity-80 uppercase tracking-wider text-[11px] font-black mr-2">Just promoted</span>
            <span className="font-display text-base">{p.name.split(" ")[0]} is now a {p.rank_name || p.designation || "Senior Partner"}</span>
            <span className="opacity-70 ml-2 text-xs">· {new Date(p.promoted_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
          </span>
        </div>
      )}

      {/* Hero — partner-first */}
      <section className="px-5 sm:px-8 pt-8 pb-10 bg-gradient-to-b from-emerald-900 to-emerald-950 text-white relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-amber-500/15 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-5xl mx-auto relative">
          <div className="flex items-start gap-5 flex-wrap">
            <div className="w-24 h-24 rounded-2xl overflow-hidden border-2 border-amber-400/30 bg-stone-700 grid place-items-center shrink-0">
              {p.photo_base64 ? (
                <img src={p.photo_base64} alt={p.name} className="w-full h-full object-cover" data-testid="storefront-partner-photo" />
              ) : (
                <span className="font-display text-3xl font-black text-amber-400">{initials || "C"}</span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400">{p.is_pl_head ? "P&L Head · Your financial advisor" : "Your financial advisor"}</div>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mt-2" data-testid="storefront-partner-name">Hi, I{"'"}m {p.name.split(" ")[0]}.</h1>
              {p.designation && (
                <div className="inline-flex items-center gap-1.5 mt-3 bg-amber-400/15 border border-amber-400/40 text-amber-300 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider" data-testid="storefront-partner-designation">
                  <Award className="w-3 h-3" /> {p.designation}
                </div>
              )}
              {p.city && <div className="flex items-center gap-1.5 text-stone-300 text-sm mt-2"><MapPin className="w-3.5 h-3.5" /> {p.city}</div>}
              {/* Bio shown verbatim per user request */}
              {p.bio && <p className="text-stone-200 text-base mt-4 leading-relaxed max-w-2xl" data-testid="storefront-partner-bio">{p.bio}</p>}
              <div className="flex flex-wrap gap-2 mt-5">
                {p.years_experience > 0 && (
                  <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur border border-white/20 px-3 py-1.5 rounded-full text-xs font-semibold" data-testid="storefront-badge-exp">
                    <Award className="w-3.5 h-3.5 text-amber-400" /> {p.years_experience}+ yrs experience
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur border border-white/20 px-3 py-1.5 rounded-full text-xs font-semibold">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-300" /> Verified Crece advisor
                </span>
                {(p.specialties || []).slice(0, 4).map((s) => (
                  <span key={s} className="bg-amber-400/15 text-amber-200 border border-amber-400/30 px-3 py-1.5 rounded-full text-xs font-semibold" data-testid={`storefront-specialty-${s}`}>{s}</span>
                ))}
              </div>
              {waUrl && (
                <a href={waUrl} target="_blank" rel="noreferrer" data-testid="storefront-wa-hero">
                  <Button className="mt-6 rounded-full bg-amber-500 hover:bg-amber-600 text-emerald-950 font-black h-11 px-6">
                    <MessageCircle className="w-4 h-4 mr-2" /> Talk to me on WhatsApp
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="px-5 sm:px-8 py-6 bg-white border-b border-stone-200 sticky top-[64px] z-30">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Search className="w-4 h-4 text-stone-400" />
            <input data-testid="storefront-search" value={search} onChange={(e) => setSearch(e.target.value)}
              className="flex-1 border-0 outline-none bg-transparent text-base placeholder:text-stone-400"
              placeholder="Search products by name or bank…" />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="storefront-category-chips">
            <button onClick={() => setActiveCat("ALL")} data-testid="storefront-chip-all"
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-bold transition-all ${activeCat === "ALL" ? "bg-emerald-900 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`}>
              All · {data.total_products}
            </button>
            {data.categories.map((c) => {
              const n = (data.products_by_category[c] || []).length;
              const cmeta = getCategoryMeta(c);
              const CIcon = cmeta.icon;
              return (
                <button key={c} onClick={() => setActiveCat(c)} data-testid={`storefront-chip-${c}`}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold transition-all ${activeCat === c ? "bg-emerald-900 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`}>
                  <CIcon className="w-3.5 h-3.5" />{c} · {n}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="px-5 sm:px-8 py-10">
        <div className="max-w-5xl mx-auto">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-16 text-stone-400" data-testid="storefront-empty">
              <Sparkles className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <div className="font-semibold">No products match your filter.</div>
              <button onClick={() => { setActiveCat("ALL"); setSearch(""); }} className="text-emerald-900 underline text-sm mt-2">Clear filters</button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="storefront-product-grid">
              {filteredProducts.map((prod) => <ProductCard key={prod.id} p={prod} onApply={onApply} />)}
            </div>
          )}
        </div>
      </section>

      {/* Final WhatsApp CTA */}
      {waUrl && (
        <section className="px-5 sm:px-8 pb-16">
          <div className="max-w-3xl mx-auto bg-gradient-to-br from-amber-50 to-amber-100/60 border-2 border-amber-300 rounded-3xl p-8 text-center">
            <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight">Not sure which product fits you?</h2>
            <p className="text-stone-700 mt-2">Message me on WhatsApp — I{"'"}ll help you pick the right one in 5 minutes.</p>
            <a href={waUrl} target="_blank" rel="noreferrer" data-testid="storefront-wa-final">
              <Button className="mt-5 rounded-full bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold h-12 px-7">
                <MessageCircle className="w-4 h-4 mr-2" /> Chat with {p.name.split(" ")[0]}
              </Button>
            </a>
          </div>
        </section>
      )}

      <StorefrontPolicyCheck code={code.toUpperCase()} partnerName={p.name} />

      <footer className="px-5 sm:px-8 py-8 text-center text-xs text-stone-500 border-t border-stone-200">
        Powered by <Link to="/" className="font-bold text-emerald-900 hover:underline">Crece</Link> · Your data is shared only with {p.name} ({p.referral_code}).
      </footer>


      <ApplyDialog open={!!applying} product={applying} code={code.toUpperCase()} partnerName={p.name} onClose={() => setApplying(null)} />
    </div>
  );
}
